"use strict";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isWeb } from "ui/src";
import { useCurrencyInfosToTokenOptions } from "components/TokenSelector/hooks/useCurrencyInfosToTokenOptions";
import { NoResultsFound } from "components/lists/NoResultsFound";
import { OnchainItemSectionName } from "components/lists/OnchainItemList/types";
import { useNftSearchResultsToNftCollectionOptions } from "components/lists/items/nfts/useNftSearchResultsToNftCollectionOptions";
import { OnchainItemListOptionType } from "components/lists/items/types";
import { useOnchainItemListSection } from "components/lists/utils";
import { useCollectionSearchQuery } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { useSearchTokensRest } from "features/dataApi/searchTokensRest";
import { SearchModalList } from "features/search/SearchModal/SearchModalList";
import {
  NUMBER_OF_RESULTS_LONG,
  NUMBER_OF_RESULTS_MEDIUM,
  NUMBER_OF_RESULTS_SHORT
} from "features/search/SearchModal/constants";
import { usePoolSearchResults } from "features/search/SearchModal/hooks/usePoolSearchResults";
import { useWalletSearchResults } from "features/search/SearchModal/hooks/useWalletSearchResults";
import { SearchTab } from "features/search/SearchModal/types";
import { SearchResultType } from "features/search/SearchResult";
import noop from "utilities/src/react/noop";
export function useSectionsForSearchResults(chainFilter, searchFilter, activeTab) {
  const searchResultsPerSection = activeTab === SearchTab.All ? NUMBER_OF_RESULTS_MEDIUM : NUMBER_OF_RESULTS_LONG;
  const {
    data: searchResultCurrencies,
    error: searchTokensError,
    refetch: refetchSearchTokens,
    loading: searchTokensLoading
  } = useSearchTokensRest({
    searchQuery: searchFilter,
    chainFilter,
    skip: !searchFilter || activeTab !== SearchTab.Tokens && activeTab !== SearchTab.All,
    size: searchResultsPerSection
  });
  const tokenSearchResults = useCurrencyInfosToTokenOptions({ currencyInfos: searchResultCurrencies });
  const tokenSearchResultsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.Tokens,
    options: tokenSearchResults
  });
  const shouldSearchPools = activeTab === SearchTab.Pools || activeTab === SearchTab.All;
  const { data: poolSearchResults, loading: poolSearchLoading } = usePoolSearchResults(
    searchFilter,
    chainFilter,
    searchResultsPerSection,
    shouldSearchPools
  );
  const poolSearchResultsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.Pools,
    options: isWeb ? poolSearchResults : []
  });
  const skipWalletSearchQuery = isWeb || activeTab !== SearchTab.Wallets && activeTab !== SearchTab.All;
  const { wallets: walletSearchResults, loading: walletSearchResultsLoading } = useWalletSearchResults(
    skipWalletSearchQuery ? "" : searchFilter ?? "",
    // skip wallet search queries on web
    chainFilter
  );
  const walletSearchOptions = walletSearchResults.map((result) => {
    switch (result.type) {
      case SearchResultType.ENSAddress:
        return { ...result, type: OnchainItemListOptionType.ENSAddress };
      case SearchResultType.Unitag:
        return { ...result, type: OnchainItemListOptionType.Unitag };
      case SearchResultType.WalletByAddress:
      default:
        return { ...result, type: OnchainItemListOptionType.WalletByAddress };
    }
  });
  const walletSearchResultsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.Wallets,
    options: walletSearchOptions
  });
  const skipNftSearchQuery = isWeb || activeTab !== SearchTab.NFTCollections && activeTab !== SearchTab.All;
  const {
    data: nftSearchResultsData,
    loading: searchNftResultsLoading,
    error: searchNftResultsError,
    refetch: refetchSearchNftResults
  } = useCollectionSearchQuery({ variables: { query: searchFilter ?? "" }, skip: skipNftSearchQuery });
  const nftCollectionOptions = useNftSearchResultsToNftCollectionOptions(nftSearchResultsData, chainFilter);
  const nftCollectionSearchResultsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.NFTCollections,
    options: activeTab === SearchTab.All ? nftCollectionOptions.slice(0, NUMBER_OF_RESULTS_SHORT) : nftCollectionOptions
  });
  const refetchAll = useCallback(async () => {
    refetchSearchTokens?.();
    await refetchSearchNftResults?.();
  }, [refetchSearchNftResults, refetchSearchTokens]);
  return useMemo(() => {
    let sections = [];
    switch (activeTab) {
      case SearchTab.All:
        if (isWeb) {
          sections = [...tokenSearchResultsSection ?? [], ...poolSearchResultsSection ?? []];
        } else {
          sections = [
            ...tokenSearchResultsSection ?? [],
            ...walletSearchResultsSection ?? [],
            ...nftCollectionSearchResultsSection ?? []
          ];
        }
        return {
          data: !searchTokensLoading && !poolSearchLoading ? sections : [],
          loading: searchTokensLoading || poolSearchLoading,
          error: !tokenSearchResults && searchTokensError || void 0,
          refetch: refetchAll
        };
      case SearchTab.Tokens:
        return {
          data: tokenSearchResultsSection ?? [],
          loading: searchTokensLoading,
          error: !tokenSearchResults && searchTokensError || void 0,
          refetch: refetchSearchTokens
        };
      case SearchTab.Pools:
        return {
          data: poolSearchResultsSection ?? [],
          loading: poolSearchLoading,
          error: void 0,
          refetch: noop
        };
      case SearchTab.Wallets:
        return {
          data: walletSearchResultsSection ?? [],
          loading: walletSearchResultsLoading,
          refetch: noop
        };
      default:
      case SearchTab.NFTCollections:
        return {
          data: nftCollectionSearchResultsSection ?? [],
          loading: searchNftResultsLoading,
          error: searchNftResultsError || void 0,
          refetch: refetchSearchNftResults
        };
    }
  }, [
    activeTab,
    nftCollectionSearchResultsSection,
    poolSearchLoading,
    poolSearchResultsSection,
    refetchAll,
    refetchSearchNftResults,
    refetchSearchTokens,
    searchNftResultsError,
    searchNftResultsLoading,
    searchTokensError,
    searchTokensLoading,
    tokenSearchResults,
    tokenSearchResultsSection,
    walletSearchResultsLoading,
    walletSearchResultsSection
  ]);
}
function _SearchModalResultsList({
  chainFilter,
  searchFilter,
  debouncedSearchFilter,
  debouncedParsedSearchFilter,
  activeTab,
  onSelect
}) {
  const { t } = useTranslation();
  const {
    data: sections,
    loading,
    error,
    refetch
  } = useSectionsForSearchResults(chainFilter, debouncedParsedSearchFilter ?? debouncedSearchFilter, activeTab);
  const userIsTyping = Boolean(searchFilter && debouncedSearchFilter !== searchFilter);
  const emptyElement = useMemo(
    () => debouncedSearchFilter ? <NoResultsFound searchFilter={debouncedSearchFilter} /> : void 0,
    [debouncedSearchFilter]
  );
  return <SearchModalList
    emptyElement={emptyElement}
    errorText={t("token.selector.search.error")}
    hasError={Boolean(error)}
    loading={userIsTyping || loading}
    refetch={refetch}
    sections={sections}
    searchFilters={{
      query: debouncedParsedSearchFilter ?? debouncedSearchFilter ?? void 0,
      searchChainFilter: chainFilter,
      searchTabFilter: activeTab
    }}
    onSelect={onSelect}
  />;
}
export const SearchModalResultsList = memo(_SearchModalResultsList);
